/* ============================================================
   CBG Collection Grid — behavior
   - cbg-facet-form: AJAX filtering via the Section Rendering API
     (adapted copy of Dawn's facets.js; the stock file is untouched)
   - cbg-price-slider: dual-handle price range slider
   - cbg-facet-remove: active-filter chips / clear-all links
   - cbg-dropdown: custom select (per-page, sort)
   - cbg-view-switcher: 1 / 2 / 4 per row grid views (localStorage)
   - cbg-collection-toolbar: SORT & FILTER bar toggle + dropdown wiring
   - cbg-filter-panel: slide-in filter panel (< 992px)
   ============================================================ */

var cbgDebounce =
  window.debounce ||
  function (fn, wait) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  };

/* ---------- AJAX facet form ---------- */

if (!customElements.get('cbg-facet-form')) {
  class CbgFacetForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.debouncedOnSubmit = cbgDebounce((event) => this.onSubmitHandler(event), 600);
      this.form.addEventListener('input', (event) => this.debouncedOnSubmit(event));
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.onSubmitHandler(event);
      });

      // SEE MORE / SEE LESS for the Subject group now lives in
      // <cbg-subject-nav> (assets/cbg-subject-nav.js) so the All Collections
      // page — which has no facet form — gets it too. Do not re-add a
      // delegated handler here: both would fire and cancel each other out.
    }

    onSubmitHandler(event) {
      const form = event.target.closest('form');
      if (!form) return;
      CbgFacetForm.syncSharedInputs(form);
      const searchParams = new URLSearchParams(new FormData(form)).toString();
      CbgFacetForm.renderPage(searchParams, event);
    }

    // Keep sort_by (and view) hidden inputs identical across both forms.
    static syncSharedInputs(sourceForm) {
      ['sort_by', 'view'].forEach((name) => {
        const source = sourceForm.elements[name];
        if (!source) return;
        document.querySelectorAll(`.cbg-facets input[name="${name}"]`).forEach((input) => {
          input.value = source.value;
        });
      });
    }

    static setListeners() {
      window.addEventListener('popstate', (event) => {
        const searchParams = event.state ? event.state.searchParams : CbgFacetForm.searchParamsInitial;
        if (searchParams === CbgFacetForm.searchParamsPrev) return;
        CbgFacetForm.renderPage(searchParams, null, false);
      });
    }

    static getSectionId() {
      const grid = document.getElementById('CbgProductGrid');
      return grid ? grid.dataset.id : null;
    }

    static renderPage(searchParams, event, updateURLHash = true) {
      CbgFacetForm.searchParamsPrev = searchParams;
      const sectionId = CbgFacetForm.getSectionId();
      if (!sectionId) return;

      const container = document.getElementById('CbgProductGridContainer');
      if (container) container.classList.add('cbg-cgrid--loading');
      CbgFacetForm.toggleActiveLinks(true);

      const url = `${window.location.pathname}?section_id=${sectionId}&${searchParams}`;
      const cached = CbgFacetForm.filterData.find((entry) => entry.url === url);

      if (cached) {
        CbgFacetForm.renderSection(cached.html, event);
      } else {
        fetch(url)
          .then((response) => response.text())
          .then((html) => {
            CbgFacetForm.filterData = [...CbgFacetForm.filterData, { html, url }];
            CbgFacetForm.renderSection(html, event);
          })
          .catch((error) => {
            console.error(error);
            if (container) container.classList.remove('cbg-cgrid--loading');
            CbgFacetForm.toggleActiveLinks(false);
          });
      }

      if (updateURLHash) {
        history.pushState(
          { searchParams },
          '',
          `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
        );
      }
    }

    static renderSection(html, event) {
      const doc = new DOMParser().parseFromString(html, 'text/html');

      // Product grid + pagination
      const container = document.getElementById('CbgProductGridContainer');
      const newContainer = doc.getElementById('CbgProductGridContainer');
      if (container && newContainer) container.innerHTML = newContainer.innerHTML;

      // Counts, active filter chips, sidebar/panel clear-all blocks
      ['CbgProductCount', 'CbgPanelTop', 'CbgActiveFacets', 'CbgNarrowHead'].forEach((id) => {
        const target = document.getElementById(id);
        const source = doc.getElementById(id);
        if (target && source) target.innerHTML = source.innerHTML;
      });

      // Filter groups, per context
      ['sidebar', 'panel'].forEach((ctx) => CbgFacetForm.syncGroups(doc, ctx));

      if (container) container.classList.remove('cbg-cgrid--loading');
      CbgFacetForm.toggleActiveLinks(false);
      CbgFacetForm.restoreFocus(event);
      CbgFacetForm.rearmScrollReveal(container);

      document.dispatchEvent(new CustomEvent('cbg:collection:rerender'));
    }

    // The cards we just injected carry .scroll-trigger, but the theme's animations.js only
    // attaches its IntersectionObserver to the elements present at DOMContentLoaded. Re-run it
    // over the new container so cards below the fold still reveal as they are scrolled to.
    // Referenced defensively: the observer is a nice-to-have, the grid must work without it.
    static rearmScrollReveal(container) {
      if (!container) return;
      if (typeof window.initializeScrollAnimationTrigger !== 'function') return;
      window.initializeScrollAnimationTrigger(container);
    }

    // Update existing groups in place (keeps each <details> open/closed state),
    // remove groups the server no longer returns, insert new ones in order.
    static syncGroups(doc, ctx) {
      const wrapper = document.getElementById(`CbgFilterGroups-${ctx}`);
      const newWrapper = doc.getElementById(`CbgFilterGroups-${ctx}`);
      if (!wrapper || !newWrapper) return;

      Array.from(wrapper.children).forEach((current) => {
        if (!doc.getElementById(current.id)) current.remove();
      });

      let previous = null;
      Array.from(newWrapper.children).forEach((incoming) => {
        const existing = document.getElementById(incoming.id);
        if (existing) {
          existing.innerHTML = incoming.innerHTML;
          previous = existing;
        } else {
          if (previous) {
            previous.after(incoming);
          } else {
            wrapper.prepend(incoming);
          }
          previous = incoming;
        }
      });
    }

    // The toggled input was replaced by the re-render — put focus back on its clone.
    static restoreFocus(event) {
      if (!event || !event.target || !event.target.id) return;
      if (event.target.isConnected) return;
      const replacement = document.getElementById(event.target.id);
      if (replacement) replacement.focus();
    }

    static toggleActiveLinks(disable) {
      document.querySelectorAll('cbg-facet-remove a').forEach((link) => {
        link.classList.toggle('disabled', disable);
      });
    }

    onActiveFilterClick(event) {
      event.preventDefault();
      const href = event.currentTarget.href;
      const searchParams = href.indexOf('?') === -1 ? '' : href.slice(href.indexOf('?') + 1);
      CbgFacetForm.renderPage(searchParams);
    }
  }

  CbgFacetForm.filterData = [];
  CbgFacetForm.searchParamsInitial = window.location.search.slice(1);
  CbgFacetForm.searchParamsPrev = window.location.search.slice(1);

  customElements.define('cbg-facet-form', CbgFacetForm);
  CbgFacetForm.setListeners();
}

/* ---------- Active filter chips / clear-all ---------- */

if (!customElements.get('cbg-facet-remove')) {
  customElements.define(
    'cbg-facet-remove',
    class CbgFacetRemove extends HTMLElement {
      connectedCallback() {
        const link = this.querySelector('a');
        if (!link || this.bound) return;
        this.bound = true;
        link.setAttribute('role', 'button');
        link.addEventListener('click', this.onClick.bind(this));
        link.addEventListener('keyup', (event) => {
          if (event.code && event.code.toUpperCase() === 'SPACE') this.onClick(event);
        });
      }

      onClick(event) {
        event.preventDefault();
        const form =
          this.closest('cbg-facet-form') || document.querySelector('cbg-facet-form');
        if (form) form.onActiveFilterClick(event);
      }
    }
  );
}

/* ---------- Dual-handle price slider ---------- */

if (!customElements.get('cbg-price-slider')) {
  customElements.define(
    'cbg-price-slider',
    class CbgPriceSlider extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.min = Number(this.dataset.min) || 0;
        this.max = Number(this.dataset.max) || 0;
        this.rangeMin = this.querySelector('[data-cbg-handle="min"]');
        this.rangeMax = this.querySelector('[data-cbg-handle="max"]');
        this.fill = this.querySelector('[data-cbg-fill]');
        this.labelMin = this.querySelector('[data-cbg-label-min]');
        this.labelMax = this.querySelector('[data-cbg-label-max]');
        const numberInputs = this.querySelectorAll('input[type="number"]');
        this.numberMin = numberInputs[0];
        this.numberMax = numberInputs[1];

        if (!this.rangeMin || !this.rangeMax || this.max <= this.min) return;

        try {
          this.formatter = new Intl.NumberFormat(this.dataset.locale || undefined, {
            style: 'currency',
            currency: this.dataset.currency,
          });
        } catch (error) {
          this.formatter = { format: (value) => value };
        }

        this.rangeMin.addEventListener('input', () => this.onInput('min'));
        this.rangeMax.addEventListener('input', () => this.onInput('max'));
        this.paint();
      }

      onInput(which) {
        let minValue = Number(this.rangeMin.value);
        let maxValue = Number(this.rangeMax.value);

        // Handles cannot cross.
        if (minValue > maxValue) {
          if (which === 'min') {
            this.rangeMin.value = maxValue;
            minValue = maxValue;
          } else {
            this.rangeMax.value = minValue;
            maxValue = minValue;
          }
        }

        // Bounds submit as empty (= filter not applied).
        if (this.numberMin) this.numberMin.value = minValue <= this.min ? '' : minValue;
        if (this.numberMax) this.numberMax.value = maxValue >= this.max ? '' : maxValue;

        this.paint();
      }

      paint() {
        const minValue = Number(this.rangeMin.value);
        const maxValue = Number(this.rangeMax.value);
        const span = this.max - this.min;
        const left = ((minValue - this.min) / span) * 100;
        const right = ((maxValue - this.min) / span) * 100;

        if (this.fill) {
          this.fill.style.left = `${left}%`;
          this.fill.style.width = `${Math.max(right - left, 0)}%`;
        }
        if (this.labelMin) this.labelMin.textContent = this.formatter.format(minValue);
        if (this.labelMax) this.labelMax.textContent = this.formatter.format(maxValue);
      }
    }
  );
}

/* ---------- Custom dropdown (per-page / sort) ---------- */

if (!customElements.get('cbg-dropdown')) {
  customElements.define(
    'cbg-dropdown',
    class CbgDropdown extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.toggle = this.querySelector('.cbg-dd__toggle');
        this.list = this.querySelector('.cbg-dd__list');
        this.label = this.querySelector('[data-dd-label]');
        if (!this.toggle || !this.list) return;

        this.onDocumentClick = this.onDocumentClick.bind(this);

        this.toggle.addEventListener('click', () => (this.isOpen() ? this.close() : this.open()));
        this.list.addEventListener('click', (event) => {
          const option = event.target.closest('[data-value]');
          if (option) this.select(option);
        });
        this.addEventListener('keydown', (event) => this.onKeydown(event));
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
      }

      isOpen() {
        return !this.list.hidden;
      }

      open() {
        this.list.hidden = false;
        this.toggle.setAttribute('aria-expanded', 'true');
        document.addEventListener('click', this.onDocumentClick);
        const current =
          this.list.querySelector('[aria-selected="true"]') || this.list.querySelector('[data-value]');
        if (current) current.focus();
      }

      close(refocus = false) {
        this.list.hidden = true;
        this.toggle.setAttribute('aria-expanded', 'false');
        document.removeEventListener('click', this.onDocumentClick);
        if (refocus) this.toggle.focus();
      }

      onDocumentClick(event) {
        if (!this.contains(event.target)) this.close();
      }

      onKeydown(event) {
        if (event.key === 'Escape' && this.isOpen()) {
          event.preventDefault();
          this.close(true);
          return;
        }
        if (!this.isOpen()) return;

        const options = Array.from(this.list.querySelectorAll('[data-value]'));
        const index = options.indexOf(document.activeElement);

        if (event.key === 'ArrowDown') {
          event.preventDefault();
          (options[index + 1] || options[0]).focus();
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          (options[index - 1] || options[options.length - 1]).focus();
        } else if (event.key === 'Enter' || event.key === ' ') {
          if (document.activeElement && document.activeElement.dataset.value) {
            event.preventDefault();
            this.select(document.activeElement);
          }
        } else if (event.key === 'Tab') {
          this.close();
        }
      }

      select(option) {
        this.list.querySelectorAll('[data-value]').forEach((element) => {
          element.setAttribute('aria-selected', element === option ? 'true' : 'false');
        });
        if (this.label) this.label.textContent = option.textContent.trim();
        this.close(true);
        this.dispatchEvent(
          new CustomEvent('cbg:dd:change', {
            bubbles: true,
            detail: { value: option.dataset.value },
          })
        );
      }
    }
  );
}

/* ---------- Toolbar: SORT & FILTER toggle + dropdown actions ---------- */

if (!customElements.get('cbg-collection-toolbar')) {
  customElements.define(
    'cbg-collection-toolbar',
    class CbgCollectionToolbar extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        const barToggle = this.querySelector('[data-cbg-bar-toggle]');
        if (barToggle) {
          barToggle.addEventListener('click', () => {
            const open = this.classList.toggle('is-open');
            barToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
          });
        }

        this.addEventListener('cbg:dd:change', (event) => {
          const dropdown = event.target.closest('cbg-dropdown');
          if (!dropdown) return;
          const value = event.detail.value;

          if (dropdown.dataset.action === 'per-page') {
            const url = new URL(window.location.href);
            if (value === dropdown.dataset.defaultValue) {
              url.searchParams.delete('view');
            } else {
              url.searchParams.set('view', value);
            }
            url.searchParams.delete('page');
            window.location.href = url.href;
          }

          if (dropdown.dataset.action === 'sort') {
            const sortInputs = document.querySelectorAll('[data-cbg-sort-input]');
            sortInputs.forEach((input) => (input.value = value));
            const firstInput = sortInputs[0];
            if (firstInput) {
              firstInput.dispatchEvent(new Event('input', { bubbles: true }));
            }
          }
        });
      }
    }
  );
}

/* ---------- Grid view switcher (1 / 2 / 4 per row) ---------- */

if (!customElements.get('cbg-view-switcher')) {
  customElements.define(
    'cbg-view-switcher',
    class CbgViewSwitcher extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.onRerender = this.onRerender.bind(this);
        this.onMediaChange = this.onMediaChange.bind(this);
        this.mobileQuery = window.matchMedia('(max-width: 767px)');

        let stored = null;
        try {
          stored = window.localStorage.getItem('cbg-collection-view');
        } catch (error) {
          stored = null;
        }
        this.current = ['1', '2', '4'].includes(stored) ? stored : '4';

        this.addEventListener('click', (event) => {
          const button = event.target.closest('[data-view]');
          if (!button) return;
          this.current = button.dataset.view;
          try {
            window.localStorage.setItem('cbg-collection-view', this.current);
          } catch (error) {
            /* storage unavailable — view is session-only */
          }
          this.apply();
        });

        document.addEventListener('cbg:collection:rerender', this.onRerender);
        this.mobileQuery.addEventListener('change', this.onMediaChange);
        this.apply();
      }

      disconnectedCallback() {
        document.removeEventListener('cbg:collection:rerender', this.onRerender);
        this.mobileQuery.removeEventListener('change', this.onMediaChange);
      }

      onRerender() {
        this.apply();
      }

      onMediaChange() {
        this.apply();
      }

      apply() {
        let view = this.current;
        if (view === '4' && this.mobileQuery.matches) view = '2';

        const grid = document.getElementById('CbgProductGrid');
        if (grid && grid.classList.contains('cbg-cgrid__grid')) {
          grid.classList.remove('cbg-cgrid__grid--view-1', 'cbg-cgrid__grid--view-2', 'cbg-cgrid__grid--view-4');
          grid.classList.add(`cbg-cgrid__grid--view-${view}`);
        }

        this.querySelectorAll('[data-view]').forEach((button) => {
          button.setAttribute('aria-pressed', button.dataset.view === view ? 'true' : 'false');
        });
      }
    }
  );
}

/* ---------- Slide-in filter panel (< 992px) ---------- */

if (!customElements.get('cbg-filter-panel')) {
  customElements.define(
    'cbg-filter-panel',
    class CbgFilterPanel extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.opener = null;

        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onKeydown);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onKeydown);
        this.setOpenState(false);
      }

      onDocumentClick(event) {
        const opener = event.target.closest('[data-cbg-panel-open]');
        if (opener) {
          this.opener = opener;
          this.open();
          return;
        }
        if (event.target.closest('[data-cbg-panel-close]')) this.close();
      }

      onKeydown(event) {
        if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
      }

      catcher() {
        return document.querySelector('.cbg-cgrid-catcher');
      }

      setOpenState(open) {
        document.documentElement.classList.toggle('cbg-cgrid-panel-open', open);
        this.classList.toggle('is-open', open);
        this.setAttribute('aria-hidden', open ? 'false' : 'true');
        const catcher = this.catcher();
        if (catcher) catcher.hidden = !open;
      }

      open() {
        this.setOpenState(true);
        const back = this.querySelector('[data-cbg-panel-close]');
        if (back) back.focus({ preventScroll: true });
      }

      close() {
        this.setOpenState(false);
        if (this.opener && this.opener.isConnected) this.opener.focus();
      }
    }
  );
}
